export type DriverLatLng = {
  lat: number;
  lng: number;
};

const reverseGeocodeCache = new Map<string, string>();

export const normalizeVehicleType = (value: unknown) =>
  String(value || "other")
    .trim()
    .toLowerCase();

export const vehicleLabel = (value: unknown) => {
  const type = normalizeVehicleType(value);

  if (type === "motorbike") return "Motorbike";
  if (type === "bicycle") return "Bicycle";
  if (type === "car") return "Car";
  if (type === "van") return "Van";
  if (type === "truck") return "Truck";
  return "Driver";
};

const svgForVehicle = (vehicleType: string, heading = 0) => {
  const type = normalizeVehicleType(vehicleType);
  const rotation = Number.isFinite(Number(heading)) ? Number(heading) : 0;

  const wheels = `
    <circle cx="13" cy="31" r="4" fill="#111827"/>
    <circle cx="35" cy="31" r="4" fill="#111827"/>
  `;

  let body = `
    <rect x="10" y="13" width="28" height="17" rx="6" fill="#2563eb"/>
    <rect x="17" y="9" width="15" height="9" rx="4" fill="#60a5fa"/>
    ${wheels}
  `;

  if (type === "motorbike") {
    body = `
      <circle cx="13" cy="31" r="5" fill="#111827"/>
      <circle cx="35" cy="31" r="5" fill="#111827"/>
      <path d="M13 31 L21 20 L30 31 L35 31 M21 20 L28 20 L31 14" stroke="#7c3aed" stroke-width="4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="22" cy="13" r="4" fill="#111827"/>
    `;
  } else if (type === "bicycle") {
    body = `
      <circle cx="12" cy="31" r="7" fill="white" stroke="#059669" stroke-width="3"/>
      <circle cx="36" cy="31" r="7" fill="white" stroke="#059669" stroke-width="3"/>
      <path d="M12 31 L20 19 L27 31 L36 31 L29 18 L20 19" stroke="#059669" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
    `;
  } else if (type === "van") {
    body = `
      <rect x="7" y="11" width="34" height="20" rx="5" fill="#0891b2"/>
      <rect x="27" y="14" width="10" height="8" rx="2" fill="#cffafe"/>
      ${wheels}
    `;
  } else if (type === "truck") {
    body = `
      <rect x="5" y="10" width="25" height="20" rx="3" fill="#ea580c"/>
      <path d="M30 16 H38 L43 23 V30 H30 Z" fill="#fb923c"/>
      <circle cx="13" cy="32" r="4" fill="#111827"/>
      <circle cx="36" cy="32" r="4" fill="#111827"/>
    `;
  }

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
      <g transform="rotate(${rotation} 24 24)">
        <circle cx="24" cy="24" r="22" fill="white" fill-opacity="0.96" stroke="#e5e7eb" stroke-width="2"/>
        ${body}
        <path d="M24 2 L28 8 H20 Z" fill="#111827"/>
      </g>
    </svg>
  `;
};

export const createVehicleMarkerIcon = (
  vehicleType?: string,
  heading?: number | null,
): google.maps.Icon | undefined => {
  if (typeof window === "undefined" || !(window as any).google?.maps) {
    return undefined;
  }

  const svg = svgForVehicle(vehicleType || "other", Number(heading || 0));

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(48, 48),
    anchor: new window.google.maps.Point(24, 24),
  };
};

export const formatLocationAge = (seconds?: number | null) => {
  if (seconds == null || !Number.isFinite(Number(seconds))) return "Unknown";

  const value = Math.max(0, Math.floor(Number(seconds)));
  if (value < 10) return "Just now";
  if (value < 60) return `${value}s ago`;
  if (value < 3600) return `${Math.floor(value / 60)}m ago`;
  return `${Math.floor(value / 3600)}h ago`;
};

export const reverseGeocodeLatLng = async (
  point: DriverLatLng,
): Promise<string> => {
  if (typeof window === "undefined" || !(window as any).google?.maps) {
    return "";
  }

  const key = `${point.lat.toFixed(5)},${point.lng.toFixed(5)}`;
  const cached = reverseGeocodeCache.get(key);
  if (cached) return cached;

  const geocoder = new window.google.maps.Geocoder();

  const result = await new Promise<string>((resolve) => {
    geocoder.geocode({ location: point }, (results, status) => {
      if (status === "OK" && results?.[0]?.formatted_address) {
        resolve(results[0].formatted_address);
        return;
      }

      resolve("");
    });
  });

  if (result) reverseGeocodeCache.set(key, result);
  return result;
};
